import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { AgContextMenuService } from 'src/app/reusables/services/ag-context-menu.service';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { IModuleManagerMenuItem, ModuleManagerMenuItemType } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { AgentStateDetailCXOneComponent } from '../../modals/agent-state-detail-cxone/agent-state-detail-cxone.component';
import { AgentStateDetailComponent } from '../../modals/agent-state-detail/agent-state-detail.component';
import { TasksModalComponent } from '../../modals/tasks-modal/tasks-modal.component';
import { CurrentAgentStatesService } from '../current-agent-states/current-agent-states.service';
import { ContactListDetailsComponent } from './contact-list-details/contact-list-details.component';
import { ContactStateCellRendererComponent } from './contact-list-details/contact-state-cell-renderer/contact-state-cell-renderer.component';
import { ContactListImageCellRendererComponent } from './contact-list-image-cell-renderer/contact-list-image-cell-renderer.component';
import { ContactListZoomComponent } from './contact-list-zoom/contact-list-zoom.component';
import { ContactListObject, DetailedStateObj } from './contact-list.models';
import { ContactListService } from './contact-list.service';

@Component({
    selector: 'cv-contact-list',
    templateUrl: './contact-list.component.html',
    styleUrls: ['./contact-list.component.scss']
})

export class ContactListComponent implements OnInit, OnDestroy {
    @Input() moduleKey: string;
    @Input() private moduleManager: ModuleManager;

    @ViewChild('contactListWidgetGrid') agGrid: AgGridAngular;
    @ViewChild("searchBox", { read: ElementRef }) searchBox: ElementRef<HTMLInputElement>;

    public isLimitNotificationShown = false;
    public isLimitNotificationClosed = false;
    public limitNotificationMessage =  "";
    public readonly MAX_WIDGET_LIMIT = 10000;
    public showFilter = false;

    public gridOptions: GridOptions;
    public dataLoadSuccess = false;
    public modalRef: NgbModalRef = null;
    public filterValue = '';
    public noData = false;

    //For cxone search placeholder localization
    public searchPlaceholder = "";
    public isAgentStateDetailModalOpen = false;

    public stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"
    public data: ContactListObject = null; // FIXME: When the endpoint properly sends over objects fit for the ag-grid, update this type
    public tableState: any;
    public gridReady = false;
    public params: any;
    public restoreInProgress = false;
    public stateSaveTimer: any;
    public isFirstLoad = true; // used for logging in the LoadData()
    public isDataLoading = false;
    public detailsLoading = false;
    public isCXOne = false;
    public subscriptions: Subscription[] = [];
    public cancelRefresh: () => void; // This function is used to resolve the cancellerRefresh promise in loadData()

    constructor(
        private ngbModal: NgbModal,
        private cvLocalizationService: CVLocalizationService,
        private agGridHelperService: AgGridHelperService,
        private contactListService: ContactListService,
        private agContextMenuService: AgContextMenuService,
        private agentStateService: CurrentAgentStatesService,
        private navigationService: NavigationService,
        public config: CvConfigurationService,
    ) { }

    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; };

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        this.isCXOne = this.config.isCxOne;

        await Promise.all([ // Load all the localization sections
            this.cvLocalizationService.GetSection('Contact_List'),
            this.cvLocalizationService.GetSection('Error_Module'),
            this.cvLocalizationService.GetSection('Common'),
            this.cvLocalizationService.GetSection('Widgets'),
        ]);

        this.limitNotificationMessage = this.cvLocalizationService.Format(
            this.cvLocalizationService.getCachedText(
                "Widgets",
                "Limit_Notification_Message_No_Export",
                "Displaying the first {0} records. Adjust filters for more precise results."
            ),
            "10,000"
        );

        this.moduleManager.setOptions({
            name: 'Contact List', // sets the title of the module (defaults to the database entry ModuleName if unset)
            onSettingsApplied: () => this.loadData(false, true),
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.VeryShort,
            onRefresh: (isAutoRefresh: boolean) => {
                return this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading, // Disable Refresh when there's already a data load in progress.
        });

        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");

        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        if (this.isCXOne) {
            this.gridOptions = await this.getCXOneBasedGridOptions();
        } else {
            this.gridOptions = await this.getPMBasedGridOptions();
        }

        this.subscriptions.push(
            this.agContextMenuService.agentDetailsClicked.subscribe(
                async (params) => {
                    // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
                    if (params.moduleKey !== this.moduleManager.getModuleKey())
                        return;

                    const response =
                        await this.agentStateService.loadCurrentAgentStatesByGroupID(
                            params.moduleKey,
                            params.groupData.groupID,
                            this.isCXOne
                        );

                    this.openAgentStateDetail(
                        params.moduleKey,
                        params.groupData.groupID,
                        response
                    );
                }
            )
        );

        this.subscriptions.push(this.agContextMenuService.createTaskClicked.subscribe( async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            this.openCreateTaskModal(params.moduleKey, params.groupData.groupID, params.groupData);
        }));

        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.tableState,
            onStateLoaded: (stateData) => {
                if (!stateData) { // No table state loaded
                    return;
                }

                this.tableState = stateData;
                this.restoreColumnState();
            },
            autoLoad: true
        });
    }

    /**
     * Change input focus to the search bar.
     */
    async focusOnSearch(): Promise<void> {
        // The element won't be ready immediately, so the focus has to be done asynchronously
        setTimeout(() => {
            if (this.config.isCxOne) {
                const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
                $(el).focus();
            }
            else {
                this.searchBox?.nativeElement?.focus();
            }
        }, 200);
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(sub => {
            sub.unsubscribe();
        });

        clearTimeout(this.stateSaveTimer);
    }

    // Converts time values from hours-minutes-seconds (hms) to seconds (03:25:50 -> 12350)
    public hmsToSeconds(time) {
        const splitTime = time.split(':');
        let seconds = 0;
        let minutes = 1;

        while (splitTime.length > 0) {
            seconds += minutes * parseInt(splitTime.pop(), 10);
            minutes *= 60;
        }

        return seconds;
    }

    public applyFilter() {
        if (this.agGrid?.api) { // notify ag-grid that the filter changed
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue.trim());
            if (this.showFilter && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0)
                this.agGrid?.api?.showNoRowsOverlay();
            else
                this.agGrid?.api?.hideOverlay();
        }

        const rowCount = this.agGrid?.api?.getDisplayedRowCount() ?? (this.isCXOne ? this.data?.QEContactListObjects?.length : this.data?.PMContactListObjects?.length) ?? 0;
        this.moduleManager.moduleOptions.nameSuffix = ` (${rowCount})`;
    }

    /**
     * Called when the clear button on the filter box is clicked.  Clears the filter
     */
    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    /** Opens the Agent State Detail modal from the Context Menu on Groups column */
    openAgentStateDetail(moduleKey: string, groupID: number, selectedRow: any) {
        if (this.isAgentStateDetailModalOpen) return;
        this.isAgentStateDetailModalOpen = true;
        this.navigationService.showOverlay = true;

        this.modalRef = this.isCXOne
            ? this.ngbModal.open(AgentStateDetailCXOneComponent,
                { size: "xl", centered: true, scrollable: true, modalDialogClass: 'cx-one-ui'  })
            : this.ngbModal.open(AgentStateDetailComponent,
                { size: "xl", centered: true, scrollable: true, modalDialogClass: null });
        this.modalRef.result.finally(() => {
            this.navigationService.showOverlay = false;
            this.isAgentStateDetailModalOpen = false;
        });

        const component = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    openCreateTaskModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(TasksModalComponent,
            {size : "xl", centered: true, scrollable: true, modalDialogClass: null});
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: TasksModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    moreOptionsEvent(e: any) {
        setTimeout(() => {
            if (this.params != null) {
                const rowIndex = this.params.rowIndex;
                const rowNode = this.params.node;
                const column = this.params.column;
                const mouseEvent = this.params.event;
                const value = this.params.value;
                this.params.api.contextMenuFactory.showMenu(rowNode, column, value, mouseEvent);
            }
        }, 500);
    }

    queueStateSave() {
        if (this.stateRestoreInProgress)
            return;

        if (this.stateSaveTimer !== false) {
            clearTimeout(this.stateSaveTimer);
        }

        this.stateSaveTimer = setTimeout(async () => {
            this.stateSaveTimer = false;
            if (await this.moduleManager.saveState('tableState')) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500);
    };

    /**
     * Retrieves the table state and saves it
     */
    updateTableState() {
        if (!this.agGrid?.api || this.restoreInProgress)
            return;

        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.agGrid?.api);
        if (tableState != null) {
            this.tableState = tableState;
        }

        // tell module manager to save the state
        this.queueStateSave();
    };

    restoreColumnState() {
        if (!this.tableState|| !this.agGrid?.api) {
            return;
        }

        // keep state from being saved while restoring
        this.restoreInProgress = true;

        this.agGridHelperService.loadGridState(this.agGrid?.api, this.tableState);

        // states can be saved again
        this.restoreInProgress = false;
    };

    async loadData(isAutoRefresh = false, cancelRefresh = false) {
        if (this.isDataLoading && !cancelRefresh)
            return;
        this.isDataLoading = true;

        // If we didn't return early, cancel the previous data load.
        if (this.cancelRefresh) {
            this.cancelRefresh();
        }
        const cancellerPromise: Promise<void> = new Promise(resolve => this.cancelRefresh = () => resolve());

        this.moduleManager.showLoader = !isAutoRefresh;
        try {
            const response = await this.contactListService.loadData(
                this.moduleKey,
                isAutoRefresh,
                this.isFirstLoad,
                this.isCXOne,
                cancellerPromise
            );
            this.cancelRefresh = null;
            this.moduleManager.setAutoRefreshReady();
            this.data = response.data;
            this.noData = this.data.QEContactListObjects.length === 0 && this.data.PMContactListObjects.length === 0;

            if (this.agGrid?.api) {
                if (this.isCXOne) {
                    this.agGrid?.api.setGridOption('rowData', this.data.QEContactListObjects);
                }
                else {
                    this.agGrid?.api.setGridOption('rowData', this.data.PMContactListObjects);
                }
            }// Else, the table isn't ready, but onGridReady should set its data
        } catch (e) {
            if (e?.code === DOMException.ABORT_ERR || e?.statusText === 'abort') {
                return;
            }
            this.moduleManager.handleLoadError(e, isAutoRefresh);
        } finally {
            this.isDataLoading = false;
            if(!this.cancelRefresh) {
                this.applyFilter();
                this.dataLoadSuccess = !this.noData;
                this.moduleManager.showLoader = false;
                this.isFirstLoad = false;
            }
        }

        const rowCount = this.agGrid?.api?.getDisplayedRowCount() ?? (this.isCXOne ? this.data?.QEContactListObjects?.length : this.data?.PMContactListObjects?.length) ?? 0;
        this.moduleManager.moduleOptions.nameSuffix = ` (${rowCount})`;

        this.showLimitNotification();
    }

    showLimitNotification(): void {
        if (!this.isLimitNotificationClosed) {
            this.isLimitNotificationShown = this.data?.QEContactListObjects.length >= this.MAX_WIDGET_LIMIT;
        }
    }

    closeLimitNotification(): void {
        this.isLimitNotificationShown = false;
        this.isLimitNotificationClosed = true;
    }

    async loadDetailedData(contactID: string): Promise<DetailedStateObj> {
        this.moduleManager.showLoader = true;
        const response = await this.contactListService.loadDetailedData(contactID, this.moduleKey);

        return response;
    }

    openModal(skill: string,
        id: number,
        agent: string,
        data: DetailedStateObj
    ) {
        const modalRef = this.ngbModal.open(
            this.config.isCxOne ? ContactListZoomComponent : ContactListDetailsComponent,
            {
                size: this.config.isCxOne ? "xl" : "md",
                modalDialogClass: this.config.isCxOne ? 'cx-one-ui' : null,
                centered: true,
                scrollable: true
            });
        const component = modalRef.componentInstance;
        component.data = data;
        if(!this.config.isCxOne){
            component.skill = skill;
            component.id = id;
            component.agent = agent;
        }
        else{
            component.contactNo = id;
        }

        this.moduleManager.showLoader = false;
        this.detailsLoading = false;
    };

    constructColumnMenuItem() {
        if (this.agGrid?.api)
            this.addColumnMenuItem();
        else
            console.error("CurrentAgentStatesTable: constructColumnMenuItem() is being called outside of gridOptions");
    };

    // Function called when the menu item is checked/unchecked to hide/show columns.
    toggleColumnVisible(i) {
        const column: ColDef = this.gridOptions.columnDefs[i]; //column clicked
        const tableStateMatch = this.tableState.columns.find(c => c.name === column.colId); //column on table
        if (tableStateMatch) {
            column.hide = !tableStateMatch.visible;
        }
        const isHidden = !tableStateMatch.visible;

        // toggle column visibility
        this.agGrid?.api.setColumnVisible(tableStateMatch.name, isHidden);

        // update table state immediately to prevent UI delay (instead of queuing)
        this.prepareTableState();
        this.queueStateSave();
    }

    prepareTableState() {
        if (!this.agGrid?.api)
            return;

        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.agGrid.api);
        if (tableState) {
            this.tableState = tableState;
        }
    }

    addColumnMenuItem() {
        if(!this.moduleManager.hasWriteAccess()) return; // Only add the menu item if the dashboard is writable

        this.moduleManager.addMenuItem({
            name: this.cvLocalizationService.getCachedText("Contact_List", "Columns", "Columns"),
            icon: "columns",
            getEnabled: () => this.gridOptions.columnDefs.length > 0 && !this.noData,
            type: ModuleManagerMenuItemType.CheckMenu,
            getCheckItems: () => this.gridOptions.columnDefs,
            getItemName: i => {
                const colState = this.gridOptions.columnDefs[i] as ColDef;
                const column = this.agGrid?.api.getColumn(colState.colId);

                if (column) // during column changes the state can have columns that are no longer in the list
                    return column['colDef'].headerName;
                else
                    return "";
            },
            isItemChecked: i => {
                if (this.gridReady && !this.tableState) {
                    this.prepareTableState();
                }
                const columnDefinition: ColDef = this.gridOptions.columnDefs[i];

                // Since the index values may be out of order if columns are reordered,
                // find the correct table state by name/field instead of relying on the index.
                const tableStateMatch = this.tableState.columns.find(c => c.name === columnDefinition.colId);
                if (tableStateMatch) {
                    columnDefinition.hide = !tableStateMatch.visible;
                }
                return !columnDefinition.hide;
            },
            onCheckClicked: i => this.toggleColumnVisible(i),
        } as IModuleManagerMenuItem);
    }

    /**
     * Shows and hides the search/filter bar
     */
    public toggleOpenSearch() { // called by moduleManager.addHeaderItem for search bar.
        if (this.showFilter) {
            this.showFilter = false;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        } else {
            this.showFilter = true;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        }
    }

    /**
     * Formats a timestamp of the form "YYYY-MM-DDTHH:mm:ss" to a string in the format YY/MM/DD
     * @param timestamp The timestamp to format
     */
    public formatTimestamp(timestamp: string): string {
        return moment(timestamp).format('YY/MM/DD').toString();
    }

    //#region Grid Options

    /**
     * Returns the grid options for the PM based contact list
     */
    public async getPMBasedGridOptions(): Promise<GridOptions<any>> {
        const timeComparator = (num1: number, num2: number) => { // Comparator used in State Time and Contact Time columns for sorting to work correctly
            const num1Seconds = this.hmsToSeconds(num1);
            const num2Seconds = this.hmsToSeconds(num2);
            if (num1Seconds === null && num2Seconds === null) {
                return 0;
            }
            if (num1Seconds === null) {
                return -1;
            }
            if (num2Seconds === null) {
                return 1;
            }
            return num1Seconds - num2Seconds;
        };

        const gridOptions = this.agGridHelperService.createGridOptions({
            context: { moduleManager: this.moduleManager },
            components: {
                ContactStateCellRendererComponent,
                ContactListImageCellRendererComponent,
                ClickableRenderer: this.agGridHelperService.createClickableGroupRenderer(
                    async (params: any) => {
                        if (this.detailsLoading) return;
                        this.detailsLoading = true;
                        const detailedData = await this.loadDetailedData(params.data.ContactID);
                        this.openModal(params.data.Skill,
                            params.data.ContactID,
                            params.data.Group.groupName,
                            detailedData,);
                        this.detailsLoading = false;
                    }
                ),
                GroupDrillDownCellRenderer: this.agGridHelperService.GroupDrillDownCellRenderer,
            },
            // grid options
            accentedSort: true,
            animateRows: true,
            suppressCellFocus: true,
            suppressScrollOnNewData: true,
            suppressContextMenu: false,
            // grid events
            onGridReady: (event: any) => {
                this.gridReady = true;
                if (this.data) {
                    if (this.isCXOne) {
                        event.api.setGridOption('rowData', this.data.QEContactListObjects);
                    } else {
                        event.api.setGridOption('rowData', this.data.PMContactListObjects);
                    }
                }

                this.stateRestoreInProgress = true; // Prevent saving while the widget is still loading the initial states

                this.restoreColumnState(); //Needed to restore columns size saved on the tableState.
                if (!this.tableState) {
                    this.agGridHelperService.sizeColumnsSmart(event); //This will trigger the publish bar only once if the gridState is null.
                    this.updateTableState();
                }

                this.constructColumnMenuItem();

                setTimeout(() => this.stateRestoreInProgress = false, 0); // Wait for the next tick to let events finish before allowing state save

                const rowCount = this.agGrid?.api?.getDisplayedRowCount() ?? (this.isCXOne ? this.data?.QEContactListObjects?.length : this.data?.PMContactListObjects?.length) ?? 0;
                this.moduleManager.moduleOptions.nameSuffix = ` (${rowCount})`;
            },
            onColumnVisible: event => {
                if (this.tableState == null || this.tableState.columns.map(c => c.width).filter(w => w != null).length === 0)
                    this.agGridHelperService.sizeColumnsSmart(event);
                this.updateTableState();
            },
            onFilterChanged: () => this.updateTableState(),
            getRowId: (params: any) => params.data.ContactID,
            onSortChanged: () => this.updateTableState(),
            onColumnMoved: () => this.updateTableState(),
            onColumnPinned: () => this.updateTableState(),
            onColumnResized: event => {
                if (event.source === "uiColumnResized") {
                    this.updateTableState();
                }
            },
            onCellClicked: (params: any) => this.params = params,
            onGridSizeChanged: event => {
                if (this.isResizeWaiting) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                    this.updateTableState();
                } else if (this.moduleManager.container.isFullscreen) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                } else {
                    this.restoreColumnState();
                }

                this.constructColumnMenuItem();
            },
            defaultColDef: {
                minWidth: this.agGridHelperService.defaultMinWidth,
            },
            // grid data
            columnDefs: [
                {
                    field: 'ContactID',
                    colId: 'ContactID',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Contact_ID', 'Contact ID'),
                    filter: 'agTextColumnFilter',
                    cellRenderer: 'ClickableRenderer',
                },
                {
                    colId: 'State',
                    field: "State",
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'State', 'State'),
                    cellRenderer: 'ContactStateCellRendererComponent',
                    // This value getter is so that ag-grid can detect when the value of the cell has changed,
                    // and so that StateCellRenderer.refresh() function gets called on such change.
                    valueGetter: params => `${params.data.State} ${params.data.StateTotal}`,
                    filter: 'agTextColumnFilter',
                },
                {
                    colId: 'State Time',
                    field: 'StateTotal',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'State_Time', 'State Time'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                    comparator: timeComparator,
                },
                {
                    field: 'Total',
                    colId: 'Total',
                    headerName: this.isCXOne ? this.cvLocalizationService.getCachedText('Contact_List', 'Contact_Time', 'Contact Time') :
                        this.cvLocalizationService.getCachedText('Contact_List', 'Total', 'Total'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                    comparator: timeComparator,
                },
                {
                    field: 'Group',
                    headerName: this.isCXOne ? this.cvLocalizationService.getCachedText('Contact_List', 'Agent_Name', 'Agent Name') :
                        this.cvLocalizationService.getCachedText('Contact_List', 'Agent', 'Agent'),
                    colId: 'Agent',
                    type: 'groupColumn',
                    cellRenderer: 'GroupDrillDownCellRenderer',
                    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => { // makes unassigned agents stay at the bottom when sorted
                        if (valueA === valueB) return 0;
                        // for null
                        else if (valueA === '<i>Unassigned</i>') {
                            return isInverted ? -1: 1;
                        }
                        else if (valueB === '<i>Unassigned</i>') {
                            return isInverted ? 1: -1;
                        }
                        else {
                            return (valueA.groupName > valueB.groupName) ? 1 : -1;
                        }
                    },
                    valueGetter: params => {
                        if (!params.node.group && params.node.data.Group.groupID === 0)
                            return `<i>${this.cvLocalizationService.getCachedText('Contact_List', 'Unassigned', 'Unassigned')}</i>`;
                        return params.data.Group;
                    },
                    getQuickFilterText: params => {
                        if (!params.value) { // if it is null filter on the value unassigned
                            return this.cvLocalizationService.getCachedText('Contact_List', 'Unassigned', 'Unassigned');
                        }
                        return params.value;
                    },
                    filterValueGetter: params => params.data.Group.groupName, // so we can filter on the agent name column since its an object
                    filter: 'agTextColumnFilter',
                },
                {
                    field: 'Skill',
                    colId: 'Skill',
                    headerName: this.isCXOne ? this.cvLocalizationService.getCachedText('Contact_List', 'Skill_Name', 'Skill Name') :
                        this.cvLocalizationService.getCachedText('Contact_List', 'Skill', 'Skill'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                },
                {
                    field: 'Campaign',
                    colId: 'Campaign',
                    headerName: this.isCXOne ? this.cvLocalizationService.getCachedText('Contact_List', 'Campaign_Name', 'Campaign Name') :
                        this.cvLocalizationService.getCachedText('Contact_List', 'Campaign', 'Campaign'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                },
                {
                    field: 'MediaType',
                    colId: 'MediaType',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Media_Type', 'Media Type'),
                    filter: 'agTextColumnFilter',
                    cellRenderer: this.config.isCxOne ? 'ContactListImageCellRendererComponent' : null,
                },
                {
                    field: 'CompanyNumber',
                    colId: 'CompanyNumber',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Company_Number', 'Company #'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                },
                {
                    field: 'CustomerNumber',
                    colId: 'CustomerNumber',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Customer_Number', 'Customer #'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                },
                {
                    field: "Direction",
                    colId: "Direction",
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Direction', 'Direction'),
                    filter: 'agTextColumnFilter',
                    cellRenderer: this.config.isCxOne ? 'ContactListImageCellRendererComponent' : null,
                },
                {
                    field: "MasterContactID",
                    colId: "MasterContactID",
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Master_Contact_ID', 'Master Contact ID'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                },
                {
                    field: 'HighProficiency',
                    colId: 'HighProficiency',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'High_Proficiency', 'High Proficiency'),
                    filter: 'agTextColumnFilter',
                    hide: true,
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : null,
                    suppressColumnsToolPanel: this.isCXOne ? false : true,
                },
                {
                    field: 'LowProficiency',
                    colId: 'LowProficiency',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Low_Proficiency', 'Low Proficiency'),
                    filter: 'agTextColumnFilter',
                    hide: true,
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : null,
                    suppressColumnsToolPanel: this.isCXOne ? false : true,
                },
                {
                    field: 'ProficiencyRoutingAttributeName',
                    colId: 'ProficiencyRoutingAttributeName',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Routing_Attribute', 'Routing Attribute'),
                    filter: 'agTextColumnFilter',
                    hide: true,
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : null,
                    suppressColumnsToolPanel: this.isCXOne ? false : true,
                },

            ],
            rowData: [],
            overlayNoRowsTemplate: this.isCXOne ?
                `<div class="error-container" style="padding-top: 48px;">
                    <svg class="mb-2">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxone-error-text">
                        ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                    </p>
                </div>` : null,
            localeText: await this.agGridHelperService.getLocalizations(),
            suppressDragLeaveHidesColumns: true,
            suppressNoRowsOverlay: true
        },
        { // second arument to CreateGridOptions
                enableCopy: true
        });

        return gridOptions;
    }

    /**
     * Returns the grid options for the CXOne based contact list
     */
    public async getCXOneBasedGridOptions(): Promise<GridOptions<any>> {
        const timeComparator = (num1: string, num2: string) => {
            const num1Seconds = this.hmsToSeconds(num1);
            const num2Seconds = this.hmsToSeconds(num2);
            if (num1Seconds === null && num2Seconds === null) {
                return 0;
            }
            if (num1Seconds === null) {
                return -1;
            }
            if (num2Seconds === null) {
                return 1;
            }
            return num1Seconds - num2Seconds;
        };

        const timeStampComparator = (valueA: string, valueB: string, nodeA: any, nodeB: any) => {
            const dateA = moment(nodeA.data.ContactDateTimeStamp, 'YYYY-MM-DDTHH:mm:ss');
            const dateB = moment(nodeB.data.ContactDateTimeStamp, 'YYYY-MM-DDTHH:mm:ss');

            if (dateA.isBefore(dateB)) {
                return -1;
            }
            if (dateA.isAfter(dateB)) {
                return 1;
            }
            return 0;
        };

        const gridOptions = this.agGridHelperService.createGridOptions({
            context: { moduleManager: this.moduleManager },
            components: {
                ContactStateCellRendererComponent,
                ContactListImageCellRendererComponent,
                ClickableRenderer: this.agGridHelperService.createClickableGroupRenderer(
                    async (params: any) => {
                        if (this.detailsLoading) return;
                        this.detailsLoading = true;
                        const detailedData = await this.loadDetailedData(params.data.ContactNo);
                        this.openModal(params.data.Skill,
                            params.data.ContactNo,
                            params.data.Group.groupName,
                            detailedData,);
                        this.detailsLoading = false;
                    }
                ),
                GroupDrillDownCellRenderer: this.agGridHelperService.GroupDrillDownCellRenderer,
            },
            getMainMenuItems: (params) => this.agGridHelperService.hideResetColumnsMenuItem(params, this.moduleManager.hasWriteAccess()),
            // grid options
            accentedSort: true,
            animateRows: true,
            suppressCellFocus: true,
            suppressScrollOnNewData: true,
            suppressContextMenu: false,
            // grid events
            onGridReady: (event: any) => {
                this.gridReady = true;
                if (this.data) {
                    event.api.setGridOption(
                        "rowData",
                        this.data.QEContactListObjects
                    );
                }

                this.stateRestoreInProgress = true; // Prevent saving while the widget is still loading the initial states

                this.restoreColumnState(); //Needed to restore columns size saved on the tableState.
                if (!this.tableState) {
                    this.agGridHelperService.sizeColumnsSmart(event); //This will trigger the publish bar only once if the gridState is null.
                    this.updateTableState();
                }

                this.constructColumnMenuItem();

                setTimeout(() => this.stateRestoreInProgress = false, 0); // Wait for the next tick to let events finish before allowing state save
            },
            onColumnVisible: event => {
                if (this.tableState == null || this.tableState.columns.map(c => c.width).filter(w => w != null).length === 0)
                    this.agGridHelperService.sizeColumnsSmart(event);
                this.updateTableState();
            },
            onFilterChanged: () => this.updateTableState(),
            getRowId: (params: any) => params.data.ContactNo,
            onSortChanged: () => this.updateTableState(),
            onColumnMoved: () => this.updateTableState(),
            onColumnPinned: () => this.updateTableState(),
            onColumnResized: event => {
                if (event.source === "uiColumnResized") {
                    this.updateTableState();
                }
            },
            onCellClicked: (params: any) => this.params = params,
            onGridSizeChanged: event => {
                if (this.isResizeWaiting) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                    this.updateTableState();
                } else if (this.moduleManager.container.isFullscreen) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                } else {
                    this.restoreColumnState();
                }

                this.constructColumnMenuItem();
            },
            defaultColDef: {
                minWidth: this.agGridHelperService.defaultMinWidth,
                menuTabs: this.agGridHelperService.getMenuTabItems(this.moduleManager.hasWriteAccess()),
            },
            // grid data
            columnDefs: [
                {
                    colId: 'ContactNo',
                    field: 'ContactNo',
                    headerName: this.cvLocalizationService.getCachedText("Contact_List", "Contact_No", "Contact No"),
                    filter: 'agTextColumnFilter',
                    cellRenderer: 'ClickableRenderer',
                },
                {
                    colId: 'State',
                    field: "State",
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'State_Name', 'State Name'),
                    cellRenderer: 'ContactStateCellRendererComponent',
                    // This value getter is so that ag-grid can detect when the value of the cell has changed,
                    // and so that StateCellRenderer.refresh() function gets called on such change.
                    valueGetter: params => `${params.data.State} ${params.data.StateTotal}`,
                    filter: 'agTextColumnFilter',
                },
                {
                    colId: 'ContactDate',
                    field: 'ContactDateDisplayValue',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Contact_Date', 'Contact Date'),
                    comparator: (valueA, valueB, nodeA, nodeB) => timeStampComparator(valueA, valueB, nodeA, nodeB),
                },
                {
                    colId: 'StateTotal',
                    field: 'StateTotal',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'State_Time', 'State Time'),
                    filter: 'agTextColumnFilter',
                    cellClass: 'cxone-line-height-36',
                    comparator: timeComparator,
                },
                {
                    colId: 'Total',
                    field: 'Total',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Contact_Time', 'Contact Time'),
                    filter: 'agTextColumnFilter',
                    cellClass: 'cxone-line-height-36' ,
                    comparator: timeComparator,
                },
                {
                    colId: 'Group',
                    field: 'Group',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Agent_Name', 'Agent Name'),
                    type: 'groupColumn',
                    cellRenderer: 'GroupDrillDownCellRenderer',
                    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => { // makes unassigned agents stay at the bottom when sorted
                        if (valueA === valueB) return 0;
                        // for null
                        else if (valueA === '<i>Unassigned</i>') {
                            return isInverted ? -1: 1;
                        }
                        else if (valueB === '<i>Unassigned</i>') {
                            return isInverted ? 1: -1;
                        }
                        else {
                            return (valueA.groupName > valueB.groupName) ? 1 : -1;
                        }
                    },
                    valueGetter: params => {
                        if (!params.node.group && params.node.data.Group.groupID === 0)
                            return `<i>${this.cvLocalizationService.getCachedText('Contact_List', 'Unassigned', 'Unassigned')}</i>`;
                        return params.data.Group;
                    },
                    getQuickFilterText: params => {
                        if (!params.value) { // if it is null filter on the value unassigned
                            return this.cvLocalizationService.getCachedText('Contact_List', 'Unassigned', 'Unassigned');
                        }
                        return params.value;
                    },
                    filterValueGetter: params => params.data.Group.groupName, // so we can filter on the agent name column since its an object
                    filter: 'agTextColumnFilter',
                },
                {
                    colId: 'Skill',
                    field: 'Skill',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Skill_Name', 'Skill Name'),
                    filter: 'agTextColumnFilter',
                    cellClass: 'cxone-line-height-36',
                    cellClassRules: {
                        'font-italic': params => params.data.Skill === 'Unassigned',
                    }
                },
                {
                    colId: 'Campaign',
                    field: 'Campaign',
                    headerName:  this.cvLocalizationService.getCachedText('Contact_List', 'Campaign_Name', 'Campaign Name'),
                    filter: 'agTextColumnFilter',
                    cellClass: 'cxone-line-height-36',
                    cellClassRules: {
                        'font-italic': params => params.data.Campaign === 'Unassigned',
                    },
                },
                {
                    colId: 'ChannelDisplayName',
                    field: 'ChannelDisplayName',
                    headerName: this.cvLocalizationService.getCachedText(
                        "Contact_List",
                        "Channel",
                        "Channel"
                    ),
                    filter: 'agTextColumnFilter',
                    cellRenderer: "ContactListImageCellRendererComponent",
                    cellClass: 'cxone-line-height-36',
                },
                {
                    colId: 'CompanyNumber',
                    field: 'CompanyNumber',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Company_Number', 'Company #'),
                    filter: 'agTextColumnFilter',
                    cellClass: 'cxone-line-height-36',
                },
                {
                    colId: 'CustomerNumber',
                    field: 'CustomerNumber',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Customer_Number', 'Customer #'),
                    filter: 'agTextColumnFilter',
                    cellClass: 'cxone-line-height-36',
                },
                {
                    colId: "Direction",
                    field: "Direction",
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Direction', 'Direction'),
                    filter: 'agTextColumnFilter',
                    cellRenderer: 'ContactListImageCellRendererComponent',
                },
                {
                    colId: "InteractionID",
                    field: "InteractionID",
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Interaction_ID', 'Interaction ID'),
                    filter: 'agTextColumnFilter',
                    cellClass: 'cxone-line-height-36',
                },
            ],
            overlayNoRowsTemplate:
                `<div class="error-container" style="padding-top: 48px;">
                    <svg class="mb-2">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxone-error-text">
                        ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                    </p>
                </div>`,
            localeText: await this.agGridHelperService.getLocalizations(),
            suppressDragLeaveHidesColumns: true,
            suppressNoRowsOverlay: true,
        },
        { // second arument to CreateGridOptions
            enableCopy: true
        });

        return gridOptions;
    }

}
